import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Moda perakendesinde AI e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "omnichannel", "first-party data", "fulfilment",
 * "benchmark" gibi yerleşik terimler de öyle. Marka adları da olduğu gibi
 * kalıyor.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
export const ebookAiInFashionRetail: WrittenPage = {
  navLabel: 'Moda perakendesinde AI',
  title: 'Lead the Shopping Season with AI in Fashion Retail',
  description:
    'Abercrombie & Fitch, Lululemon ve Pandora sezonu şimdiden yeniden kuruyor. Moda perakendesinin liderleri dijital yatırımı nasıl ölçülebilir performansa çeviriyor? Digitopia Impact Labs’in e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Perakende liderleri sezona nasıl hazırlanıyor?',
      headline: 'Lead the Shopping Season with AI in Fashion Retail',
      body: [
        'Abercrombie & Fitch, Lululemon ve Pandora sezonu şimdiden yeniden kuruyor.',
        'Moda perakendesinin liderlerinin dijital yatırımlarını nasıl ölçülebilir performansa '
          + 'çevirdiğini keşfedin. Farklı olarak ne yapıyorlar ve siz aradaki farkı nasıl '
          + 'kapatırsınız?',
      ],
      ctas: [{ label: 'İndirin', href: LINKS.fashionRetailEbookPdf }],
      photo: {
        src: '/photo/ai-fashion-retail-shopping.webp',
        alt:
          'Güneş gözlüklü bir alışverişçi, elinde kâğıt ve kumaş poşetlerle; akşamüstü bir '
          + 'alışveriş merkezinin cam cephesi önünde aşağıdan çekilmiş.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden indirmelisiniz?',
      body: [
        'Alışveriş sezonu, moda perakendecileri için yılın tamamını belirliyor. Ama başarı '
          + 'yalnızca yeni koleksiyonlardan ya da düzgün işleyen bir tedarik zincirinden ibaret '
          + 'değil.',
        'Bugün moda perakendesinde AI hem içeriği hem ticareti şekillendiriyor; fulfilment’ı '
          + 'çalıştırıyor, ekiplerin yetkinliğini yükseltiyor. Liderler, diğerlerinin hâlâ pilot '
          + 'olarak denediği şeyi çoktan işin içine almış durumda.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.fashionRetailEbookPdf }],
      photo: {
        src: '/photo/ai-fashion-retail-ebook-covers.webp',
        alt:
          'Moda perakendesi e-kitabının üç kopyası, biri diğerinin arkasında, beyaz zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      sub: 'Bu kısa analizin içinde şunları bulacaksınız:',
      points: [
        'AI’ın pazarlama metninden ürün beden asistanlarına kadar her şeyi nasıl etkilediği',
        'Omnichannel fulfilment’ın neden artık bir marka farkı olduğu, iyi olsa da olur bir '
          + 'özellik değil',
        'Sadakat programlarının ve first-party data’nın gerçek zamanlı kişiselleştirmeyi nasıl '
          + 'beslediği',
        'Sahadaki ekiplerin dijital olarak nasıl eğitildiği ve güçlendirildiği',
        'Öne çıkan markaların operating model’lerini dijitalin üzerine değil, dijitalin '
          + 'etrafında nasıl kurduğu',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.fashionRetailEbookPdf }],
      photo: {
        src: '/photo/ai-fashion-retail-ebook-cover.webp',
        alt:
          'Moda perakendesi e-kitabı ayakta duruyor; kapağı siyah ve pembe bir zemin önünde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Öne çıkanlar',
      sub: 'Raporda şunları göreceksiniz:',
      points: [
        'Abercrombie & Fitch talep tahmininde hava durumu ve sosyal medya sinyallerini nasıl '
          + 'kullanıyor',
        'Lululemon saha ekiplerini dijital sertifikalarla nasıl eğitiyor',
        'Pandora AI destekli kampanyaları görülmemiş bir hızda nasıl devreye alıyor',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.fashionRetailEbookPdf }],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Markanızı AI destekli stratejilerle sezonun zirvesine hazırlayın',
      sub:
        'Moda ve lüks segmentin liderleri alışveriş sezonuna nasıl hazırlanıyor, görün ve kendi '
        + 'hazırlığınızı benchmark edin. Stratejilerin tamamı için rehberi indirin.',
      cta: { label: 'E-kitabı indirin', href: LINKS.fashionRetailEbookPdf },
    },
    {
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'Bu rapor yalnızca bilgilendirme ve benchmark amaçlıdır. Digitopia’nın adı geçen '
        + 'markalarla bir bağı yoktur ve bu markalar tarafından desteklenmemektedir.',
      ],
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            '2021’den bu yana stratejik önceliklendirme bizim için temel bir odak oldu. '
            + 'Perakende, lojistik ve e-ticaret gibi iş birimlerimizin maturity seviyeleri '
            + 'birbirinden farklı olduğu için hizalanma kritik. Değerlendirmeyi tam bir '
            + 'şeffaflıkla yapmak, yolda kalmamıza ve bilgiye dayalı kararlar vermemize '
            + 'yardımcı oluyor.',
          source: 'Şerafettin Özer, Chief Digital and Information Officer',
          company: 'LC Waikiki',
          logo: { name: 'LC Waikiki', src: '/logo/lcwaikikicard.png' },
          portrait: { src: '/photo/portrait-serafettin-ozer.png', alt: 'Şerafettin Özer' },
        },
      ],
    },
  ],
}
